import {
  hashKey,
  useIsFetching,
  useQueryClient,
  type DataTag,
  type QueryKey,
} from "@-/com.npmjs.tanstack__react-query";
import { useCallback, useMemo } from "react";

/**
 * A gated query result: the value a gate (the ONE render-time observer of a nullable query) has
 * already narrowed, plus the key of the query that produced it.
 *
 * Children render `data`, which is non-null by construction. They never re-read the same query:
 * a second observer of a nullable query can disagree with the gate (e.g. an offline-mode switch
 * re-reads against a smaller static DB and returns `null`). `queryKey` keeps the provenance and
 * carries the RAW (nullable) data type, so status reads and invalidation work, and any fresh read
 * through it (`queryClient.getQueryData(queryKey)`) is forced to handle `null` again.
 */
export type ResolvedQuery<TQueryData> = {
  readonly data: NonNullable<TQueryData>;
  readonly queryKey: DataTag<QueryKey, TQueryData, Error>;
};

/**
 * Builds a {@link ResolvedQuery} for a gate's provider. Memoized on the data identity and the
 * query key's HASH: query-option objects are fresh every render (`createUseQueryOptions` returns a
 * new object with a lazy `queryKey` getter), so keying on identity would change the handle — and
 * re-render every selector of it — on every gate render.
 */
export function useResolvedQuery<TQueryData>(
  queryKey: DataTag<QueryKey, TQueryData, Error>,
  data: NonNullable<TQueryData>,
): ResolvedQuery<TQueryData> {
  const queryKeyHash = hashKey(queryKey);
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- keyed on the key's hash, not its per-render identity.
  return useMemo(() => ({ data, queryKey }), [data, queryKeyHash]);
}

/** Whether the query behind a {@link ResolvedQuery} is (re)fetching right now. */
export function useResolvedQueryIsFetching(resolved: ResolvedQuery<unknown>): boolean {
  return useIsFetching({ exact: true, queryKey: resolved.queryKey }) > 0;
}

/** Invalidates (and so refetches, while observed) the query behind a {@link ResolvedQuery}. */
export function useInvalidateResolvedQuery(resolved: ResolvedQuery<unknown>): () => Promise<void> {
  const queryClient = useQueryClient();
  const { queryKey } = resolved;
  return useCallback(
    () => queryClient.invalidateQueries({ exact: true, queryKey }),
    [queryClient, queryKey],
  );
}
